/**
 * A sample S/4HANA response.
 *
 * Written in SAP's own field names and SAP's own conventions — unsigned
 * amounts with a separate debit/credit indicator, string amounts where the
 * service returns strings, accounts as six-digit strings. A sample that has
 * already been tidied into the shape this platform wants proves nothing about
 * a connector, because the tidying is the part that goes wrong.
 *
 * It carries on purpose the things a real extract carries: a reversed
 * document and its reversal, a line with no indicator, a line in a foreign
 * currency, and an account no mapping rule covers. Each of those is a way a
 * connector quietly loses or invents money, and each is pinned by a test.
 *
 * DEMONSTRATION FIGURES. Nothing here is Madinah's money.
 */
import type { JournalLine, PlanLine, SapResponse } from "../domain/sap";

const YEAR = "2026";
const PERIOD = "009";

/**
 * Document numbers run in sequence, not at random.
 *
 * A random number here would give the sample a different document on every
 * render: the screen would appear to change when nothing had happened, and a
 * test that pinned one would fail on a Tuesday for no reason.
 */
let nextDocument = 1_000_800;

function line(
  account: string,
  name: string,
  amount: number | string,
  dc: "S" | "H",
  extra: Partial<JournalLine> = {},
): JournalLine {
  return {
    CompanyCode: "1710",
    FiscalYear: YEAR,
    FiscalPeriod: PERIOD,
    AccountingDocument: extra.AccountingDocument ?? String(nextDocument++),
    GLAccount: account,
    GLAccountName: name,
    AmountInCompanyCodeCurrency: amount,
    CompanyCodeCurrency: "SAR",
    DebitCreditCode: dc,
    PostingDate: `${YEAR}-09-18`,
    DocumentDate: `${YEAR}-09-16`,
    ...extra,
  };
}

function plan(account: string, amount: number): PlanLine {
  return {
    CompanyCode: "1710",
    FiscalYear: YEAR,
    FiscalPeriod: PERIOD,
    GLAccount: account,
    AmountInCompanyCodeCurrency: amount,
    CompanyCodeCurrency: "SAR",
    PlanningCategory: "PLN01",
  };
}

/**
 * The lines.
 *
 * Revenue is credited and cost is debited, which is the convention the whole
 * read rests on. If somebody ever "fixes" this sample by making revenue a
 * debit, the tests will pass and the connector will be wrong.
 */
export function sapLines(): JournalLine[] {
  nextDocument = 1_000_800;
  return [
    /* aeronautical revenue — credits */
    line("400100", "Aircraft landing charges", 18_420_000, "H", { ProfitCenter: "PC-AERO" }),
    line("400200", "Passenger service charge", 26_180_000, "H", { ProfitCenter: "PC-AERO" }),
    line("400300", "Aircraft parking charges", 3_240_000, "H", { ProfitCenter: "PC-AERO" }),
    /* the service returns some amounts as strings, with separators */
    line("401100", "Ground handling concession", "4,860,000", "H", { ProfitCenter: "PC-AERO" }),

    /* commercial revenue */
    line("410100", "Retail concession income", 9_640_000, "H", { ProfitCenter: "PC-COMM" }),
    line("410200", "Food and beverage concession", 5_180_000, "H", { ProfitCenter: "PC-COMM" }),
    line("411000", "Car park income", 3_920_000, "H", { ProfitCenter: "PC-COMM" }),
    line("412000", "Advertising income", 1_460_000, "H", { ProfitCenter: "PC-COMM" }),

    /* other income */
    line("420100", "Property rental", 2_310_000, "H", { ProfitCenter: "PC-PROP" }),

    /* staff — debits */
    line("500100", "Salaries and wages", 21_400_000, "S", { CostCenter: "CC-1000" }),
    line("500200", "Social insurance", 2_680_000, "S", { CostCenter: "CC-1000" }),
    line("500400", "Overtime", 1_940_000, "S", { CostCenter: "CC-2200" }),

    /* maintenance — the one the platform can check against its own records */
    line("510100", "Airfield maintenance", 6_120_000, "S", { CostCenter: "CC-2200" }),
    line("510200", "Terminal maintenance", 4_380_000, "S", { CostCenter: "CC-2100" }),
    line("510300", "Maintenance materials and spares", 2_260_000, "S", { CostCenter: "CC-2200" }),
    line("511000", "Plant and equipment repair", 1_840_000, "S", { CostCenter: "CC-2300" }),

    /* utilities */
    line("520100", "Electricity", 8_960_000, "S", { CostCenter: "CC-3000" }),
    line("520200", "Water", 1_120_000, "S", { CostCenter: "CC-3000" }),
    line("520300", "Fuel, generators and plant", 1_640_000, "S", { CostCenter: "CC-3000" }),

    /* contracted services */
    line("530100", "Security services", 7_240_000, "S", { CostCenter: "CC-4000" }),
    line("530200", "Cleaning services", 4_160_000, "S", { CostCenter: "CC-4100" }),
    line("530400", "Waste and grounds", 1_280_000, "S", { CostCenter: "CC-4100" }),

    /* administration */
    line("540100", "Insurance", 2_940_000, "S", { CostCenter: "CC-5000" }),
    line("540300", "Professional fees", 1_480_000, "S", { CostCenter: "CC-5000" }),
    line("540500", "Information technology", 3_260_000, "S", { CostCenter: "CC-5100" }),

    /* capital, in the asset block */
    line("160100", "Assets under construction — runway 17/35", 14_600_000, "S"),
    line("160200", "Assets under construction — baggage hall", 8_240_000, "S"),

    /* a balance sheet account an operator would not want in a management figure */
    line("800100", "Trade receivables clearing", 6_400_000, "S"),

    /* ── the awkward lines ─────────────────────────────────────────────── */

    /* a document posted and then reversed: both sides are in the extract */
    line("530200", "Cleaning services", 940_000, "S", {
      AccountingDocument: "1000841",
      IsReversed: true,
      CostCenter: "CC-4100",
    }),
    line("530200", "Cleaning services — reversal", 940_000, "H", {
      AccountingDocument: "1000842",
      IsReversal: true,
      CostCenter: "CC-4100",
    }),

    /* an indicator the extract lost: the amount is real, the direction is not known */
    line("510400", "Lift and escalator maintenance", 1_360_000, "S", { DebitCreditCode: "" }),

    /* a line posted in the group currency, not the company code's own */
    {
      ...line("540300", "Professional fees — overseas counsel", 180_000, "S"),
      CompanyCodeCurrency: "USD",
    },

    /* an account outside every rule: its money belongs to nobody until somebody says where */
    line("700400", "Airline incentive scheme", 3_180_000, "S", { CostCenter: "CC-6000" }),
    line("700500", "Route development fund", 1_240_000, "S", { CostCenter: "CC-6000" }),
  ];
}

/**
 * The plan, covering three of the four cases on purpose.
 *
 * An operator plans at the level it manages, not account by account, and the
 * three states that produces all have to be visible or the screen has only
 * been tested against the easy one:
 *
 *   fully planned  aeronautical, other revenue, staff, utilities — every
 *                  account that posts an actual has a plan line, so a
 *                  variance means something
 *   partly planned commercial and maintenance and contracted services — one
 *                  plan line against a block that posts several actuals. Set
 *                  against the whole line it would report a large overspend
 *                  that is purely an artefact of the comparison, so no
 *                  variance is computed and the coverage is shown instead
 *   not planned    administration, which has no plan line at all
 *
 * Overtime is planned low and spent high, which is the kind of finding the
 * fully planned case exists to surface.
 */
export function sapPlan(): PlanLine[] {
  return [
    /* aeronautical: every posting account planned */
    plan("400100", 19_000_000),
    plan("400200", 25_000_000),
    plan("400300", 3_400_000),
    plan("401100", 4_500_000),

    /* commercial: one plan line against four posting accounts */
    plan("410100", 9_000_000),

    /* other revenue: one account, one plan line */
    plan("420100", 2_600_000),

    /* staff: fully planned, and overtime is where it goes wrong */
    plan("500100", 21_000_000),
    plan("500200", 2_700_000),
    plan("500400", 1_200_000),

    /* maintenance: planned at the block, spent across four accounts */
    plan("510100", 6_000_000),

    /* utilities: fully planned */
    plan("520100", 8_200_000),
    plan("520200", 1_100_000),
    plan("520300", 1_500_000),

    /* contracted services: partly planned */
    plan("530100", 7_000_000),

    /* administration: no plan line at all */
  ];
}

/**
 * The response, with its read time anchored to the clock.
 *
 * A fixture with a fixed timestamp is fresh on the day it is written and a
 * year stale afterwards, and the freshness line beside every figure would
 * read as a broken connection to anyone opening this next month. Anchored
 * instead: the sample is always four hours old.
 */
export function sapResponse(now: Date = new Date(), options: { plan?: boolean } = {}): SapResponse {
  return {
    system: "S/4HANA · OEMA production (sample)",
    service: "API_OPLACCTGDOCITEMCUBE_SRV",
    readAt: new Date(now.getTime() - 4 * 3_600_000).toISOString(),
    period: {
      companyCode: "1710",
      fiscalYear: YEAR,
      fiscalPeriod: PERIOD,
      state: "open",
    },
    lines: sapLines(),
    plan: options.plan === false ? undefined : sapPlan(),
  };
}
